import { useCallback, useEffect, useRef, useState } from 'react'
import { BotMessageSquare } from 'lucide-react'
import { useNavigate } from 'react-router'
import { isWebView } from '@/shared/bridge/env'
import { cn } from '@/shared/utils/cn'
import { AiAssistantFrame } from './AiAssistantFrame'
import { useOverlayHistoryDismiss } from '@/shared/ui/useOverlayHistoryDismiss'

interface AiButtonProps {
  className?: string
}

export function AiButton({ className }: AiButtonProps) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const closePanel = useCallback(() => setOpen(false), [])
  // 1:1 문의 모달처럼 뒤로가기(브라우저 백)로 패널을 닫는다.
  const dismiss = useOverlayHistoryDismiss(open, closePanel, 'ai-panel')
  const rootRef = useRef<HTMLDivElement>(null)

  // 패널 열린 동안 바깥 클릭하면 닫기(클릭은 그대로 통과).
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) dismiss()
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open, dismiss])

  // 앱(WebView): 카톡식 풀페이지 라우트로 진입(네이티브 백버튼이 history 따라 자동 처리).
  // 웹: 헤더 버튼에서 패널 토글.
  const handlePress = () => {
    if (isWebView()) navigate('/ai')
    else if (open) dismiss()
    else setOpen(true)
  }

  return (
    <div ref={rootRef} className="relative shrink-0">
      {open && (
        <div className="qsc-ai-panel-in fixed right-3 top-[calc(var(--safe-top)+3.75rem)] z-[9996] h-[calc(100dvh-var(--safe-top)-var(--mobile-bottom-nav-height)-4.75rem)] max-h-[45rem] w-[min(28.75rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 lg:right-7 lg:top-16 lg:h-[calc(100dvh-5rem)]">
          <AiAssistantFrame onClose={dismiss} />
        </div>
      )}

      <button
        type="button"
        aria-label="AI 도우미 열기"
        aria-pressed={open}
        onClick={handlePress}
        className={cn(
          'qsc-ai-chroma inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 text-preset-label font-extrabold text-text shadow-card transition-transform duration-base hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
          className,
        )}
      >
        <BotMessageSquare aria-hidden className="size-4 text-brand" />
        <span className="qsc-ai-label leading-none">AI</span>
      </button>
    </div>
  )
}
